import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, Form, Input, Toast, Steps, Selector } from 'antd-mobile'
import { validatePhone, validateVerificationCode } from '../utils/validation'

/**
 * 手环激活页面 - 完整流程
 *
 * URL 示例: https://h.blet.cc/qUN6Vy3
 * braceletId 从 URL 路径中提取: "qUN6Vy3"
 *
 * 流程:
 * 1. 验证码验证
 * 2. 填写个人信息
 * 3. 激活成功
 */

type ActivationStep = 'verify' | 'info' | 'success'

interface UserInfo {
  name: string
  age: string
  gender: string
  blood_type: string
  phone: string
  emergency_contact_name: string
  emergency_contact_phone: string
  emergency_contact_relation: string
  address: string
  medical_conditions?: string
  allergies?: string
  medications?: string
}

interface ValidationErrors {
  verificationCode?: string
  name?: string
  age?: string
  gender?: string
  blood_type?: string
  phone?: string
  emergency_contact_name?: string
  emergency_contact_phone?: string
  emergency_contact_relation?: string
}

export default function ActivationPage() {
  const { braceletId } = useParams<{ braceletId: string }>()
  const navigate = useNavigate()

  const [currentStep, setCurrentStep] = useState<ActivationStep>('verify')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<ValidationErrors>({})
  const [userInfo, setUserInfo] = useState<UserInfo>({
    name: '',
    age: '',
    gender: '',
    blood_type: '',
    phone: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    address: '',
    medical_conditions: '',
    allergies: '',
    medications: '',
  })

  // 实时验证函数
  const validateField = (field: keyof ValidationErrors, value: string) => {
    const newErrors = { ...errors }

    switch (field) {
      case 'verificationCode':
        if (!value) {
          newErrors.verificationCode = '请输入验证码'
        } else if (!validateVerificationCode(value.toUpperCase())) {
          newErrors.verificationCode = '验证码必须是6位大写字母或数字'
        } else {
          delete newErrors.verificationCode
        }
        break

      case 'name':
        if (!value) {
          newErrors.name = '请输入姓名'
        } else if (value.length > 50) {
          newErrors.name = '姓名不能超过50个字符'
        } else {
          delete newErrors.name
        }
        break

      case 'age':
        if (!value) {
          newErrors.age = '请输入年龄'
        } else {
          const ageNum = parseInt(value)
          if (isNaN(ageNum) || ageNum < 0 || ageNum > 150) {
            newErrors.age = '请输入有效的年龄（0-150）'
          } else {
            delete newErrors.age
          }
        }
        break

      case 'gender':
        if (!value) {
          newErrors.gender = '请选择性别'
        } else if (!['男', '女', '其他'].includes(value)) {
          newErrors.gender = '请选择有效的性别'
        } else {
          delete newErrors.gender
        }
        break

      case 'blood_type':
        if (!value) {
          newErrors.blood_type = '请选择血型'
        } else if (!['A', 'B', 'AB', 'O', '不详'].includes(value)) {
          newErrors.blood_type = '请选择有效的血型'
        } else {
          delete newErrors.blood_type
        }
        break

      case 'phone':
        if (!value) {
          newErrors.phone = '请输入手机号'
        } else if (!validatePhone(value)) {
          newErrors.phone = '请输入正确的11位手机号'
        } else {
          delete newErrors.phone
        }
        break

      case 'emergency_contact_name':
        if (!value) {
          newErrors.emergency_contact_name = '请输入紧急联系人姓名'
        } else if (value.length > 50) {
          newErrors.emergency_contact_name = '姓名不能超过50个字符'
        } else {
          delete newErrors.emergency_contact_name
        }
        break

      case 'emergency_contact_phone':
        if (!value) {
          newErrors.emergency_contact_phone = '请输入紧急联系人电话'
        } else if (!validatePhone(value)) {
          newErrors.emergency_contact_phone = '请输入正确的11位手机号'
        } else if (value === userInfo.phone) {
          newErrors.emergency_contact_phone = '紧急联系人电话不能与本人电话相同'
        } else {
          delete newErrors.emergency_contact_phone
        }
        break

      case 'emergency_contact_relation':
        if (!value) {
          newErrors.emergency_contact_relation = '请选择与紧急联系人的关系'
        } else {
          delete newErrors.emergency_contact_relation
        }
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // 步骤 1: 验证码验证
  const handleVerify = async () => {
    // 使用实时验证
    validateField('verificationCode', verificationCode)
    if (!validateVerificationCode(verificationCode.toUpperCase())) {
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/api/bracelets/${braceletId}/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_code: verificationCode }),
      })

      const data = await response.json()

      if (response.ok) {
        Toast.show({ icon: 'success', content: '验证成功！' })
        setCurrentStep('info')
      } else {
        Toast.show({
          icon: 'fail',
          content: data.detail || data.message || '验证码错误',
        })
      }
    } catch (error) {
      console.error('Verification error:', error)
      Toast.show({ icon: 'fail', content: '网络错误，请重试' })
    } finally {
      setLoading(false)
    }
  }

  // 步骤 2: 提交个人信息并激活
  const handleActivate = async () => {
    // 验证所有必填字段
    validateField('name', userInfo.name)
    validateField('age', userInfo.age)
    validateField('gender', userInfo.gender)
    validateField('blood_type', userInfo.blood_type)
    validateField('phone', userInfo.phone)
    validateField('emergency_contact_name', userInfo.emergency_contact_name)
    validateField('emergency_contact_phone', userInfo.emergency_contact_phone)
    validateField('emergency_contact_relation', userInfo.emergency_contact_relation)

    // 检查是否有验证错误
    const hasNameError = !userInfo.name || userInfo.name.length > 50
    const hasAgeError = !userInfo.age || parseInt(userInfo.age) < 0 || parseInt(userInfo.age) > 150
    const hasGenderError = !userInfo.gender || !['男', '女', '其他'].includes(userInfo.gender)
    const hasBloodTypeError = !userInfo.blood_type || !['A', 'B', 'AB', 'O', '不详'].includes(userInfo.blood_type)
    const hasPhoneError = !validatePhone(userInfo.phone)
    const hasEmergencyNameError = !userInfo.emergency_contact_name
    const hasEmergencyPhoneError = !validatePhone(userInfo.emergency_contact_phone) ||
                                   userInfo.emergency_contact_phone === userInfo.phone
    const hasEmergencyRelationError = !userInfo.emergency_contact_relation

    if (hasNameError || hasAgeError || hasGenderError || hasBloodTypeError || hasPhoneError || hasEmergencyNameError || hasEmergencyPhoneError || hasEmergencyRelationError) {
      Toast.show({ icon: 'fail', content: '请检查并修正表单中的错误' })
      return
    }

    setLoading(true)
    try {
      const response = await fetch(`/api/bracelets/${braceletId}/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          verification_code: verificationCode,
          user_info: {
            name: userInfo.name,
            age: parseInt(userInfo.age),
            gender: userInfo.gender,
            blood_type: userInfo.blood_type,
            phone: userInfo.phone,
            emergency_contact_name: userInfo.emergency_contact_name,
            emergency_contact_phone: userInfo.emergency_contact_phone,
            emergency_contact_relation: userInfo.emergency_contact_relation,
            address: userInfo.address,
            medical_conditions: userInfo.medical_conditions || '',
            allergies: userInfo.allergies || '',
            medications: userInfo.medications || '',
          },
        }),
      })

      const data = await response.json()

      if (response.ok) {
        Toast.show({ icon: 'success', content: '激活成功！' })
        setCurrentStep('success')
      } else {
        Toast.show({
          icon: 'fail',
          content: data.detail || data.message || '激活失败',
        })
      }
    } catch (error) {
      console.error('Activation error:', error)
      Toast.show({ icon: 'fail', content: '网络错误，请重试' })
    } finally {
      setLoading(false)
    }
  }

  // 渲染验证码步骤
  const renderVerifyStep = () => (
    <div className="bg-white border border-gray-200 rounded-xl p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">输入验证码</h2>

      {/* 醒目提示：验证码位置 */}
      <div className="bg-orange-50 border-2 border-orange-400 rounded-lg p-4 mb-6">
        <div className="flex items-center">
          <div className="flex-shrink-0 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center mr-3">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <p className="text-lg font-bold text-orange-800">请在手环内侧查找验证码</p>
            <p className="text-base text-orange-700">翻看手环内侧，找到 6 位字母数字组合</p>
          </div>
        </div>
      </div>

      <Form layout="vertical">
        <Form.Item>
          <div className="mb-3">
            <label className="text-lg font-medium text-gray-700">验证码</label>
          </div>
          <Input
            placeholder="请输入验证码"
            maxLength={6}
            value={verificationCode}
            onChange={(val) => {
              setVerificationCode(val.toUpperCase())
              validateField('verificationCode', val)
            }}
            onBlur={() => validateField('verificationCode', verificationCode)}
            style={{
              '--font-size': '20px',
              '--text-align': 'center',
              fontSize: '20px',
              height: '56px',
              letterSpacing: '0.3em',
              borderRadius: '12px',
              borderColor: errors.verificationCode ? '#ff4d4f' : undefined
            }}
          />
          {errors.verificationCode && (
            <div className="text-red-500 text-sm mt-2">{errors.verificationCode}</div>
          )}
        </Form.Item>

        <Button
          block
          color="primary"
          size="large"
          loading={loading}
          onClick={handleVerify}
          style={{
            height: '56px',
            fontSize: '18px',
            fontWeight: 'bold',
            marginTop: '24px',
            borderRadius: '12px'
          }}
        >
          验证并继续
        </Button>
      </Form>

      <div className="mt-8 p-5 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="font-bold text-blue-900 mb-3 text-lg">温馨提示</h3>
        <ul className="space-y-2 text-base text-blue-800">
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>验证码在手环背面，共 6 位字符</span>
          </li>
          <li className="flex items-start">
            <span className="mr-2">•</span>
            <span>验证成功后填写佩戴者信息</span>
          </li>
        </ul>
      </div>
    </div>
  )

  // 渲染信息填写步骤
  const renderInfoStep = () => (
    <div className="bg-white border border-gray-200 rounded-xl p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-2">填写佩戴者信息</h2>
      <p className="text-base text-gray-500 mb-8">请填写准确信息，以便紧急时联系</p>

      <Form layout="vertical">
        <Form.Item>
          <label className="text-lg font-medium text-gray-900 block mb-2">
            姓名 <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="请输入姓名"
            value={userInfo.name}
            maxLength={50}
            onChange={(val) => {
              setUserInfo({ ...userInfo, name: val })
              validateField('name', val)
            }}
            onBlur={() => validateField('name', userInfo.name)}
            style={{
              height: '50px',
              fontSize: '17px',
              borderRadius: '10px',
              borderColor: errors.name ? '#ff4d4f' : undefined
            }}
          />
          {errors.name && (
            <div className="text-red-500 text-sm mt-1">{errors.name}</div>
          )}
        </Form.Item>

        <Form.Item>
          <label className="text-lg font-medium text-gray-900 block mb-2">
            年龄 <span className="text-red-500">*</span>
          </label>
          <Input
            type="number"
            placeholder="请输入年龄"
            value={userInfo.age}
            onChange={(val) => {
              setUserInfo({ ...userInfo, age: val })
              validateField('age', val)
            }}
            onBlur={() => validateField('age', userInfo.age)}
            style={{
              height: '50px',
              fontSize: '17px',
              borderRadius: '10px',
              borderColor: errors.age ? '#ff4d4f' : undefined
            }}
          />
          {errors.age && (
            <div className="text-red-500 text-sm mt-1">{errors.age}</div>
          )}
        </Form.Item>

        <Form.Item>
          <label className="text-lg font-medium text-gray-900 block mb-2">
            性别 <span className="text-red-500">*</span>
          </label>
          <Selector
            options={[
              { label: '男', value: '男' },
              { label: '女', value: '女' },
              { label: '其他', value: '其他' },
            ]}
            value={userInfo.gender ? [userInfo.gender] : []}
            onChange={(val) => {
              const selected = val[0] || ''
              setUserInfo({ ...userInfo, gender: selected })
              validateField('gender', selected)
            }}
            style={{
              '--border-radius': '10px',
              '--border': errors.gender ? '1px solid #ff4d4f' : '1px solid #e5e7eb',
            }}
          />
          {errors.gender && (
            <div className="text-red-500 text-sm mt-1">{errors.gender}</div>
          )}
        </Form.Item>

        <Form.Item>
          <label className="text-lg font-medium text-gray-900 block mb-2">
            血型 <span className="text-red-500">*</span>
          </label>
          <Selector
            options={[
              { label: 'A型', value: 'A' },
              { label: 'B型', value: 'B' },
              { label: 'AB型', value: 'AB' },
              { label: 'O型', value: 'O' },
              { label: '未知', value: '不详' },
            ]}
            value={userInfo.blood_type ? [userInfo.blood_type] : []}
            onChange={(val) => {
              const selected = val[0] || ''
              setUserInfo({ ...userInfo, blood_type: selected })
              validateField('blood_type', selected)
            }}
            style={{
              '--border-radius': '10px',
              '--border': errors.blood_type ? '1px solid #ff4d4f' : '1px solid #e5e7eb',
            }}
          />
          {errors.blood_type && (
            <div className="text-red-500 text-sm mt-1">{errors.blood_type}</div>
          )}
        </Form.Item>

        <Form.Item>
          <label className="text-lg font-medium text-gray-900 block mb-2">
            联系电话 <span className="text-red-500">*</span>
          </label>
          <Input
            type="tel"
            placeholder="请输入手机号"
            maxLength={11}
            value={userInfo.phone}
            onChange={(val) => {
              setUserInfo({ ...userInfo, phone: val })
              validateField('phone', val)
            }}
            onBlur={() => validateField('phone', userInfo.phone)}
            style={{
              height: '50px',
              fontSize: '17px',
              borderRadius: '10px',
              borderColor: errors.phone ? '#ff4d4f' : undefined
            }}
          />
          {errors.phone && (
            <div className="text-red-500 text-sm mt-1">{errors.phone}</div>
          )}
        </Form.Item>

        <div className="border-t border-gray-200 my-6 pt-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">紧急联系人</h3>

          <Form.Item>
            <label className="text-lg font-medium text-gray-900 block mb-2">
              联系人姓名 <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="请输入联系人姓名"
              value={userInfo.emergency_contact_name}
              maxLength={50}
              onChange={(val) => {
                setUserInfo({ ...userInfo, emergency_contact_name: val })
                validateField('emergency_contact_name', val)
              }}
              onBlur={() => validateField('emergency_contact_name', userInfo.emergency_contact_name)}
              style={{
                height: '50px',
                fontSize: '17px',
                borderRadius: '10px',
                borderColor: errors.emergency_contact_name ? '#ff4d4f' : undefined
              }}
            />
            {errors.emergency_contact_name && (
              <div className="text-red-500 text-sm mt-1">{errors.emergency_contact_name}</div>
            )}
          </Form.Item>

          <Form.Item>
            <label className="text-lg font-medium text-gray-900 block mb-2">
              联系人电话 <span className="text-red-500">*</span>
            </label>
            <Input
              type="tel"
              placeholder="请输入联系人手机号"
              maxLength={11}
              value={userInfo.emergency_contact_phone}
              onChange={(val) => {
                setUserInfo({ ...userInfo, emergency_contact_phone: val })
                validateField('emergency_contact_phone', val)
              }}
              onBlur={() => validateField('emergency_contact_phone', userInfo.emergency_contact_phone)}
              style={{
                height: '50px',
                fontSize: '17px',
                borderRadius: '10px',
                borderColor: errors.emergency_contact_phone ? '#ff4d4f' : undefined
              }}
            />
            {errors.emergency_contact_phone && (
              <div className="text-red-500 text-sm mt-1">{errors.emergency_contact_phone}</div>
            )}
          </Form.Item>

          <Form.Item>
            <label className="text-lg font-medium text-gray-900 block mb-2">
              与联系人关系 <span className="text-red-500">*</span>
            </label>
            <Selector
              options={[
                { label: '配偶', value: '配偶' },
                { label: '子女', value: '子女' },
                { label: '父母', value: '父母' },
                { label: '兄弟姐妹', value: '兄弟姐妹' },
                { label: '亲属', value: '亲属' },
                { label: '朋友', value: '朋友' },
                { label: '邻居', value: '邻居' },
                { label: '护工', value: '护工' },
                { label: '其他', value: '其他' },
              ]}
              value={userInfo.emergency_contact_relation ? [userInfo.emergency_contact_relation] : []}
              onChange={(val) => {
                const selected = val[0] || ''
                setUserInfo({ ...userInfo, emergency_contact_relation: selected })
                validateField('emergency_contact_relation', selected)
              }}
              style={{
                '--border-radius': '10px',
                '--border': errors.emergency_contact_relation ? '1px solid #ff4d4f' : '1px solid #e5e7eb',
              }}
            />
            {errors.emergency_contact_relation && (
              <div className="text-red-500 text-sm mt-1">{errors.emergency_contact_relation}</div>
            )}
          </Form.Item>
        </div>

        <div className="border-t border-gray-200 my-6 pt-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">补充信息</h3>
          <p className="text-sm text-gray-500 mb-4">选填，可提高救助效率</p>

          <Form.Item>
            <label className="text-base font-medium text-gray-700 block mb-2">
              居住地址
            </label>
            <Input
              placeholder="选填"
              value={userInfo.address}
              onChange={(val) => setUserInfo({ ...userInfo, address: val })}
              style={{ height: '48px', fontSize: '16px', borderRadius: '10px' }}
            />
          </Form.Item>

          <Form.Item>
            <label className="text-base font-medium text-gray-700 block mb-2">
              既往病史
            </label>
            <Input
              placeholder="如：高血压、糖尿病等（选填）"
              value={userInfo.medical_conditions}
              onChange={(val) => setUserInfo({ ...userInfo, medical_conditions: val })}
              style={{ height: '48px', fontSize: '16px', borderRadius: '10px' }}
            />
          </Form.Item>

          <Form.Item>
            <label className="text-base font-medium text-gray-700 block mb-2">
              药物过敏
            </label>
            <Input
              placeholder="如：青霉素过敏等（选填）"
              value={userInfo.allergies}
              onChange={(val) => setUserInfo({ ...userInfo, allergies: val })}
              style={{ height: '48px', fontSize: '16px', borderRadius: '10px' }}
            />
          </Form.Item>

          <Form.Item>
            <label className="text-base font-medium text-gray-700 block mb-2">
              常用药物
            </label>
            <Input
              placeholder="如：降压药等（选填）"
              value={userInfo.medications}
              onChange={(val) => setUserInfo({ ...userInfo, medications: val })}
              style={{ height: '48px', fontSize: '16px', borderRadius: '10px' }}
            />
          </Form.Item>
        </div>

        <div className="flex gap-3 mt-8">
          <Button
            color="default"
            onClick={() => setCurrentStep('verify')}
            style={{ flex: 1, height: '52px', fontSize: '17px', borderRadius: '10px' }}
          >
            上一步
          </Button>
          <Button
            block
            color="primary"
            size="large"
            loading={loading}
            onClick={handleActivate}
            style={{ flex: 2, height: '52px', fontSize: '17px', fontWeight: 'bold', borderRadius: '10px' }}
          >
            完成激活
          </Button>
        </div>

        <div className="text-center mt-4 text-xs text-gray-500">
          点击"完成激活"即表示您已阅读并同意
          <a href="/tos-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 mx-1">服务条款</a>
          和
          <a href="/privacypolicy-zh.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 mx-1">隐私政策</a>
        </div>
      </Form>
    </div>
  )

  // 渲染成功步骤
  const renderSuccessStep = () => (
    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
      <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500 rounded-full mb-6">
        <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h3 className="text-3xl font-bold text-gray-900 mb-3">激活成功！</h3>
      <p className="text-lg text-gray-600 mb-8">
        您的手环已成功激活，佩戴者信息已录入系统。
      </p>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-left">
        <h4 className="text-xl font-bold text-gray-900 mb-4">温馨提示</h4>
        <ul className="space-y-3 text-base text-gray-700">
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-blue-600 font-bold text-sm">1</span>
            </div>
            <span className="ml-3 leading-relaxed">请妥善保管手环，确保佩戴者随身佩戴</span>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-blue-600 font-bold text-sm">2</span>
            </div>
            <span className="ml-3 leading-relaxed">他人扫码即可查看紧急信息</span>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
              <span className="text-blue-600 font-bold text-sm">3</span>
            </div>
            <span className="ml-3 leading-relaxed">您可以随时访问此页面更新信息</span>
          </li>
        </ul>
      </div>

      <div className="flex gap-4">
        <Button
          color="default"
          onClick={() => navigate(`/query/${braceletId}`)}
          style={{
            flex: 1,
            height: '52px',
            fontSize: '17px',
            borderRadius: '10px'
          }}
        >
          查看信息
        </Button>
        <Button
          color="primary"
          onClick={() => { window.location.href = '/' }}
          style={{
            flex: 1,
            height: '52px',
            fontSize: '17px',
            fontWeight: 'bold',
            borderRadius: '10px'
          }}
        >
          返回首页
        </Button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-white p-4">
      <div className="max-w-2xl mx-auto pt-6">
        {/* 顶部Logo */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-3">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            手环激活
          </h1>
          <p className="text-lg text-gray-600">
            {currentStep === 'verify' && '验证设备所有权'}
            {currentStep === 'info' && '填写佩戴者信息'}
            {currentStep === 'success' && '激活成功'}
          </p>
          {/* 手环ID - 弱化显示 */}
          <div className="mt-2">
            <span className="text-xs text-gray-400">设备编号：{braceletId}</span>
          </div>
        </div>

        {/* 进度指示器 */}
        {currentStep !== 'success' && (
          <div className="mb-8">
            <Steps
              current={currentStep === 'verify' ? 0 : 1}
              direction="horizontal"
              style={{
                '--title-font-size': '15px',
                '--description-font-size': '13px',
              }}
            >
              <Steps.Step title="验证" description="输入验证码" />
              <Steps.Step title="填写" description="佩戴者信息" />
              <Steps.Step title="完成" description="激活成功" />
            </Steps>
          </div>
        )}

        {/* 步骤内容 */}
        {currentStep === 'verify' && renderVerifyStep()}
        {currentStep === 'info' && renderInfoStep()}
        {currentStep === 'success' && renderSuccessStep()}
      </div>
    </div>
  )
}
